import mergeRefs from 'merge-refs';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, type Transition } from 'motion/react';
import { rd, type RemoteData } from '@passionware/monads';
import { cn } from 'v5/platform/dom/cn';
import { keepAnimationInJs } from 'v5/platform/react/keepAnimationInJs';
import {
  MediaLoadErrorOverlay,
  MediaLoadPendingOverlay
} from 'v5/patterns/feedback/MediaLoadStateOverlays';
import { MediaBackdrop } from './MediaBackdrop';
import { usePictureFit } from './usePictureFit';

/** Props passed to a custom ImageComponent (no DOM drag/etc to avoid conflict with motion). */
export interface ImagePreviewImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  onLoad?: () => void;
  onError?: () => void;
  initial?: { opacity: number };
  animate?: { opacity: number };
  transition?: Transition;
  onUpdate?: () => void;
  /** Marks the picture itself, never its backdrop or fallback (what a host's hover zooms). */
  'data-media-picture'?: string;
}

export interface ImagePreviewProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  /** Image URL. When null/undefined, only placeholder or fallback is shown. */
  src?: string | null;
  alt?: string;
  /** Object fit for the image. @default 'cover' */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  /**
   * With `cover`: a picture smaller than the box isn't enlarged to cover it; it shows whole,
   * centred on its blurred copy (`MediaBackdrop`), its corners as round as the ground beside it
   * allows.
   */
  noUpscale?: boolean;
  /** Native img loading. @default 'lazy' */
  loading?: 'lazy' | 'eager';
  /** Content when no src or when error. Renders inside the same layout. */
  fallback?: React.ReactNode;
  /** Optional callback when image has loaded. */
  onLoad?: () => void;
  /** Optional callback when image fails to load. */
  onError?: () => void;
  /** Custom image element. Receives img props plus motion props. @default motion.img */
  ImageComponent?: React.ComponentType<ImagePreviewImageProps>;
}

export const ImagePreview = forwardRef<HTMLDivElement, ImagePreviewProps>(
  (
    {
      src,
      alt = '',
      objectFit = 'cover',
      noUpscale = false,
      loading = 'lazy',
      fallback,
      className,
      style,
      onLoad,
      onError,
      ImageComponent: ImageComponentProp = motion.img,
      ...rest
    },
    ref
  ) => {
    const hasSrc = !!src;
    const containerRef = useRef<HTMLDivElement>(null);
    /** The image was loaded before it was shown: no skeleton, no fade. */
    const [instant, setInstant] = useState(false);

    const [state, setState] = useState<RemoteData<void>>(() =>
      hasSrc ? rd.ofPending() : rd.ofError(new Error('No image'))
    );
    const { fit, radius, measure } = usePictureFit(containerRef);

    useLayoutEffect(() => {
      if (!hasSrc) {
        setState(rd.ofError(new Error('No image')));
        return;
      }
      // An image the browser already holds (a local blob, a cache hit) is
      // complete before the first paint.
      const img = containerRef.current?.querySelector(':scope > img');
      const loaded =
        img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0;
      setInstant(loaded);
      setState(loaded ? rd.of(undefined) : rd.ofPending());
      if (loaded) measure(img);
    }, [src, hasSrc, measure]);

    const handleImgLoad = () => {
      const img = containerRef.current?.querySelector(':scope > img');
      if (img instanceof HTMLImageElement) measure(img);
      setState(rd.of(void 0));
      onLoad?.();
    };

    const handleImgError = () => {
      setState(rd.ofError(new Error('Image failed to load')));
      onError?.();
    };

    const showSkeleton = hasSrc && rd.isPending(state);
    const showError = !hasSrc || rd.isError(state);
    const showImage = hasSrc && rd.isSuccess(state);
    const small =
      noUpscale &&
      objectFit === 'cover' &&
      fit !== undefined &&
      fit !== 'covers';

    const objectFitClass =
      objectFit === 'cover'
        ? 'object-cover'
        : objectFit === 'contain'
          ? 'object-contain'
          : objectFit === 'fill'
            ? 'object-fill'
            : 'object-none';

    return (
      <div
        ref={mergeRefs(ref, containerRef)}
        className={cn(
          'relative overflow-hidden bg-bg-subtle flex items-center justify-center min-h-0 min-w-0',
          className
        )}
        style={style}
        {...rest}
      >
        {small && showImage && (
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: instant ? 0 : 0.25 }}
            onUpdate={keepAnimationInJs}
          >
            <MediaBackdrop src={src} />
          </motion.div>
        )}
        {hasSrc && (
          <ImageComponentProp
            src={src}
            alt={alt}
            data-media-picture=""
            className={
              small
                ? 'relative max-h-full max-w-full object-contain'
                : cn('absolute inset-0 size-full', objectFitClass)
            }
            // as round as the ground beside it allows, up to the media dialog's corners
            style={small ? { borderRadius: radius } : undefined}
            loading={loading}
            decoding="async"
            onLoad={handleImgLoad}
            onError={handleImgError}
            initial={{ opacity: 0 }}
            animate={{
              opacity: showImage ? 1 : 0
            }}
            onUpdate={keepAnimationInJs}
            transition={{
              duration: showImage && !instant ? 0.25 : 0,
              ease: [0.25, 0.1, 0.25, 1]
            }}
          />
        )}

        <AnimatePresence>
          {showSkeleton && (
            <motion.div
              key="skeleton"
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onUpdate={keepAnimationInJs}
            >
              <MediaLoadPendingOverlay />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showError && (
            <motion.div
              key="error"
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onUpdate={keepAnimationInJs}
            >
              {fallback ?? <MediaLoadErrorOverlay />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

ImagePreview.displayName = 'ImagePreview';
